/**
 * Features Page — PatrolPuffin Marketing Portal
 *
 * Deep-dive feature page with 10 discrepancy types, carrier support,
 * how-it-works, and CTA. Uses i18n and analytics tracking.
 *
 * @module portals/marketing/pages/FeaturesPage
 */

import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { getBrand } from '@puffin-core/brand-assets';
import {
  MarketingSeo,
  CTASection,
  useAnalytics,
  webPage,
  breadcrumbList,
} from '@puffin-core/marketing-shell';
import { PATROL_MARKETING_ROUTES } from '../constants/patrolMarketingRoutePaths';

const APP_KEY = 'patrol';
const FEATURES_TITLE = 'Features - PatrolPuffin Carrier Invoice Audit';
const FEATURES_DESCRIPTION =
  'Detect carrier billing discrepancies across USPS, UPS, and FedEx. Automated rate mismatch and DIM weight audit.';
const FEATURES_CANONICAL = 'https://patrolpuffin.com/features';

/**
 * Discrepancy type keys for iteration
 * @constant {Array<string>}
 */
const DISCREPANCY_KEYS = [
  'rateMismatch',
  'dimWeight',
  'lateDelivery',
  'duplicateCharge',
  'surchargeError',
  'addressCorrection',
  'voidNotCredited',
  'zoneError',
  'weightDiscrepancy',
  'serviceDowngrade',
];

/**
 * Carrier keys for iteration
 * @constant {Array<string>}
 */
const CARRIER_KEYS = ['usps', 'ups', 'fedex'];

/**
 * How-it-works step keys
 * @constant {Array<Object>}
 */
const STEP_KEYS = [
  {
    number: 1,
    titleKey: 'features.howItWorks.step1Title',
    descKey: 'features.howItWorks.step1Desc',
  },
  {
    number: 2,
    titleKey: 'features.howItWorks.step2Title',
    descKey: 'features.howItWorks.step2Desc',
  },
  {
    number: 3,
    titleKey: 'features.howItWorks.step3Title',
    descKey: 'features.howItWorks.step3Desc',
  },
];

/**
 * Features hero section
 *
 * @param {Object} props - Component props
 * @param {Function} props.t - Translation function
 * @returns {React.JSX.Element} Hero section
 */
const FeaturesHero = ({ t }) => (
  <section className="hero-section">
    <div className="hero-content">
      <h1 className="hero-title">{t('features.hero.title')}</h1>
      <p className="hero-subtitle">{t('features.hero.subtitle')}</p>
    </div>
  </section>
);

FeaturesHero.propTypes = {
  t: PropTypes.func.isRequired,
};

/**
 * Discrepancy card sub-component
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Discrepancy type title
 * @param {string} props.description - Discrepancy type description
 * @returns {React.JSX.Element} Discrepancy card
 */
const DiscrepancyCard = ({ title, description }) => (
  <div className="feature-card">
    <h3 className="feature-card-title">{title}</h3>
    <p className="feature-card-description">{description}</p>
  </div>
);

DiscrepancyCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
};

/**
 * Discrepancy types section with 10 cards
 *
 * @param {Object} props - Component props
 * @param {Function} props.t - Translation function
 * @returns {React.JSX.Element} Discrepancies section
 */
const DiscrepanciesSection = ({ t }) => (
  <section className="section-marketing" id="discrepancies">
    <div className="section-heading-group">
      <h2 className="section-heading">{t('features.discrepancies.title')}</h2>
    </div>
    <div className="feature-card-grid">
      {DISCREPANCY_KEYS.map((key) => (
        <DiscrepancyCard
          key={key}
          title={t(`features.discrepancies.${key}`)}
          description={t(`features.discrepancies.${key}Desc`)}
        />
      ))}
    </div>
  </section>
);

DiscrepanciesSection.propTypes = {
  t: PropTypes.func.isRequired,
};

/**
 * Carrier card sub-component
 *
 * @param {Object} props - Component props
 * @param {string} props.name - Carrier name
 * @param {string} props.description - Carrier description
 * @returns {React.JSX.Element} Carrier card
 */
const CarrierCard = ({ name, description }) => (
  <div className="config-card">
    <h3 className="config-card-name">{name}</h3>
    <p className="config-card-description">{description}</p>
  </div>
);

CarrierCard.propTypes = {
  name: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
};

/**
 * Carrier support section
 *
 * @param {Object} props - Component props
 * @param {Function} props.t - Translation function
 * @returns {React.JSX.Element} Carriers section
 */
const CarriersSection = ({ t }) => (
  <section className="section-marketing section-light" id="carriers">
    <div className="section-heading-group">
      <h2 className="section-heading">{t('features.carriers.title')}</h2>
    </div>
    <div className="config-grid">
      {CARRIER_KEYS.map((key) => (
        <CarrierCard
          key={key}
          name={t(`features.carriers.${key}`)}
          description={t(`features.carriers.${key}Desc`)}
        />
      ))}
    </div>
  </section>
);

CarriersSection.propTypes = {
  t: PropTypes.func.isRequired,
};

/**
 * How it works section for the features page
 *
 * @param {Object} props - Component props
 * @param {Function} props.t - Translation function
 * @returns {React.JSX.Element} How it works section
 */
const FeaturesHowItWorks = ({ t }) => (
  <section className="section-marketing" id="how-it-works">
    <div className="section-heading-group">
      <h2 className="section-heading">{t('features.howItWorks.title')}</h2>
    </div>
    <div className="how-it-works-grid">
      {STEP_KEYS.map((step) => (
        <div key={step.number} className="how-it-works-step">
          <span className="step-number">{step.number}</span>
          <h3 className="step-title">{t(step.titleKey)}</h3>
          <p className="step-description">{t(step.descKey)}</p>
        </div>
      ))}
    </div>
  </section>
);

FeaturesHowItWorks.propTypes = {
  t: PropTypes.func.isRequired,
};

/**
 * Features Page Component
 *
 * Detailed feature breakdown with discrepancy types, carriers,
 * how-it-works, and CTA.
 *
 * @returns {React.JSX.Element} Features page
 */
const FeaturesPage = () => {
  // `getBrand` is a plain registry read (not a hook), so this memo carries NO
  // hook dependency — `brand` moves WITH it, which is what the old disable
  // ("brand must be resolved before the memo") missed. It therefore belongs
  // ahead of the custom hooks, per the canonical
  // state → effect → computed → custom order. Still evaluated at RENDER time
  // (not module load), so suites that stub `@puffin-core/marketing-shell` keep
  // resolving `webPage` / `breadcrumbList`.
  const brand = getBrand(APP_KEY);

  const jsonLd = useMemo(
    () => [
      webPage({
        appKey: APP_KEY,
        brand,
        url: FEATURES_CANONICAL,
        title: FEATURES_TITLE,
        description: FEATURES_DESCRIPTION,
      }),
      breadcrumbList({
        crumbs: [
          { name: 'Home', url: 'https://patrolpuffin.com/' },
          { name: 'Features', url: FEATURES_CANONICAL },
        ],
      }),
    ],
    [brand],
  );

  const { t } = useTranslation();
  const { trackPageView } = useAnalytics();

  useEffect(() => {
    trackPageView('/features', 'PatrolPuffin Features');
  }, [trackPageView]);

  const ogImageUrl = `https://patrolpuffin.com${brand.assets.ogCard}`;

  return (
    <>
      <MarketingSeo
        title={FEATURES_TITLE}
        description={FEATURES_DESCRIPTION}
        canonical={FEATURES_CANONICAL}
        ogType="website"
        ogImage={ogImageUrl}
        jsonLd={jsonLd}
      />

      <FeaturesHero t={t} />
      <DiscrepanciesSection t={t} />
      <CarriersSection t={t} />
      <FeaturesHowItWorks t={t} />
      <CTASection
        heading={t('features.cta.title')}
        subheading={t('features.cta.subtitle')}
        primaryAction={t('features.cta.button')}
        primaryLink={PATROL_MARKETING_ROUTES.CONTACT}
      />
    </>
  );
};

export default FeaturesPage;
